import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCurrentApplicationYear, filterYearsForUser } from '@/utils/academic-year-utils';
import { useActiveAcademicYears } from '../hooks/useAcademicYears';
import { useApplicationPeriodStatus } from '../hooks/useApplicationPeriod';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/components/context/AuthContext';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';
import { toast } from 'sonner';

interface AcademicYearContextType {
  currentYear: string;
  setCurrentYear: (year: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  refetch?: () => void;
  availableYears?: string[];
  globalSchoolYear?: string | null;
  allowSchoolOverride?: boolean;
  allowSchoolAdjacentYears?: boolean;
  globalAdminYear?: string | null;
  allowAdminOverride?: boolean;
  allowAdminAdjacentYears?: boolean;
}

const AcademicYearContext = createContext<AcademicYearContextType | undefined>(undefined);

// Local storage keys
const STORAGE_KEYS = {
  SYSTEM_SETTINGS: 'systemSettings',
  CACHED_CURRENT_YEAR: 'cached_current_academic_year',
  CACHED_CURRENT_YEAR_TIMESTAMP: 'cached_current_academic_year_timestamp',
  LEGACY_DEFAULT_YEAR: 'defaultAcademicYear'
};

// Cache duration for offline support
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Synchronously read year from localStorage for initial state
// This prevents race conditions where API data loads before localStorage is checked
const getInitialYearFromStorage = (): string => {
  try {
    // Check systemSettings first (primary storage)
    const settings = localStorage.getItem(STORAGE_KEYS.SYSTEM_SETTINGS);
    if (settings) {
      const parsed = JSON.parse(settings);
      if (parsed.schoolYears?.currentSchoolYear) {
        return parsed.schoolYears.currentSchoolYear;
      }
    }

    // Check cached year
    const cachedYear = localStorage.getItem(STORAGE_KEYS.CACHED_CURRENT_YEAR);
    const cachedTimestamp = localStorage.getItem(STORAGE_KEYS.CACHED_CURRENT_YEAR_TIMESTAMP);
    if (cachedYear && cachedTimestamp) {
      const age = Date.now() - parseInt(cachedTimestamp);
      if (age < CACHE_DURATION) {
        return cachedYear;
      }
    }

    // Check legacy storage
    const legacyYear = localStorage.getItem(STORAGE_KEYS.LEGACY_DEFAULT_YEAR);
    if (legacyYear) {
      return legacyYear;
    }
  } catch (error) {
    console.warn('[AcademicYear] Error reading initial year from localStorage:', error);
    EnhancedErrorLogger.getInstance().logQuickError(error, 'AcademicYear', { context: 'read initial year' });
  }
  return '';
};

export const AcademicYearProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize with localStorage value to prevent race conditions
  const initialYear = getInitialYearFromStorage();
  const [currentYear, setCurrentYearState] = useState<string>(initialYear);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [globalSchoolYear, setGlobalSchoolYear] = useState<string | null>(null);

  // Ref to track if user has explicitly selected a year - prevents reset during refetch operations
  // Initialize to true if we already have a year from localStorage
  const yearHasBeenSetRef = useRef(!!initialYear);
  const [allowSchoolOverride, setAllowSchoolOverride] = useState<boolean>(true);
  const [allowSchoolAdjacentYears, setAllowSchoolAdjacentYears] = useState<boolean>(false);
  const [globalAdminYear, setGlobalAdminYear] = useState<string | null>(null);
  const [allowAdminOverride, setAllowAdminOverride] = useState<boolean>(true);
  const [allowAdminAdjacentYears, setAllowAdminAdjacentYears] = useState<boolean>(false);
  const [globalSettingsChecked, setGlobalSettingsChecked] = useState(false);
  const [hasSyncedToDatabase, setHasSyncedToDatabase] = useState(false);
  const [syncAttempts, setSyncAttempts] = useState(0);
  const { userRoles } = useAuth();
  const queryClient = useQueryClient();
  
  // Use the API hook to get active academic years
  const { 
    data: academicYearsData, 
    isLoading: isLoadingAPI, 
    error: apiError,
    refetch 
  } = useActiveAcademicYears();

  // Use user preferences hook for database persistence
  const { 
    selectedAcademicYear: userSelectedYear, 
    updateSelectedYear,
    isLoading: isLoadingPreferences,
    isAuthenticated 
  } = useUserPreferences();

  // Extract available years from API response
  const allAvailableYears = academicYearsData?.data?.active?.map((year: any) => year.year) || [];
  
  // Apply filtering based on global settings and user role
  const isSchoolUser = userRoles?.some(role => 
    ['school', 'school_admin', 'school_staff'].includes(role)
  );
  const isAdminUser = userRoles?.some(role => 
    ['admin', 'super_admin', 'district', 'district_admin'].includes(role)
  );

  let availableYears = allAvailableYears;
  if (globalSettingsChecked) {
    if (isSchoolUser) {
      availableYears = filterYearsForUser(
        allAvailableYears,
        globalSchoolYear,
        allowSchoolOverride,
        allowSchoolAdjacentYears
      );
    } else if (isAdminUser) {
      availableYears = filterYearsForUser(
        allAvailableYears,
        globalAdminYear,
        allowAdminOverride,
        allowAdminAdjacentYears
      );
    }
  }
  
  // Determine the current academic year from API data
  const apiCurrentYear = academicYearsData?.data?.current?.year;

  // Helper function to read from localStorage
  const readFromLocalStorage = useCallback((): string | null => {
    try {
      // First check systemSettings (primary storage)
      const settings = localStorage.getItem(STORAGE_KEYS.SYSTEM_SETTINGS);
      if (settings) {
        const parsed = JSON.parse(settings);
        if (parsed.schoolYears?.currentSchoolYear) {
          return parsed.schoolYears.currentSchoolYear;
        }
      }
      
      // Check cached current year
      const cachedYear = localStorage.getItem(STORAGE_KEYS.CACHED_CURRENT_YEAR);
      const cachedTimestamp = localStorage.getItem(STORAGE_KEYS.CACHED_CURRENT_YEAR_TIMESTAMP);
      
      if (cachedYear && cachedTimestamp) {
        const age = Date.now() - parseInt(cachedTimestamp);
        if (age < CACHE_DURATION) {
          return cachedYear;
        }
      }
      
      // Check legacy storage for migration.
      // Reading it was never enough: the legacy key is only deleted by
      // writeToLocalStorage, so a session that never changes the year left it
      // in place forever and kept falling back to it every time the fresher
      // cache expired — pinning people to a year they set long ago. Finish the
      // migration here instead, at the moment the value is actually used.
      const legacyYear = localStorage.getItem(STORAGE_KEYS.LEGACY_DEFAULT_YEAR);
      if (legacyYear) {
        try {
          localStorage.setItem(STORAGE_KEYS.CACHED_CURRENT_YEAR, legacyYear);
          localStorage.setItem(STORAGE_KEYS.CACHED_CURRENT_YEAR_TIMESTAMP, Date.now().toString());
          localStorage.removeItem(STORAGE_KEYS.LEGACY_DEFAULT_YEAR);
        } catch {
          // A full or read-only store must not stop us returning the year.
        }
        return legacyYear;
      }
    } catch (error) {
      console.warn('[AcademicYear] Error reading from localStorage:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AcademicYear', { context: 'read localStorage' });
    }
    
    return null;
  }, []);

  // Helper function to write to localStorage
  const writeToLocalStorage = useCallback((year: string) => {
    try {
      // Update systemSettings
      const settings = localStorage.getItem(STORAGE_KEYS.SYSTEM_SETTINGS);
      const updatedSettings = settings 
        ? { ...JSON.parse(settings), schoolYears: { ...JSON.parse(settings).schoolYears, currentSchoolYear: year } }
        : { schoolYears: { currentSchoolYear: year } };
      localStorage.setItem(STORAGE_KEYS.SYSTEM_SETTINGS, JSON.stringify(updatedSettings));
      
      // Update cache
      localStorage.setItem(STORAGE_KEYS.CACHED_CURRENT_YEAR, year);
      localStorage.setItem(STORAGE_KEYS.CACHED_CURRENT_YEAR_TIMESTAMP, Date.now().toString());
      
      // Remove legacy storage if it exists
      localStorage.removeItem(STORAGE_KEYS.LEGACY_DEFAULT_YEAR);
    } catch (error) {
      console.error('[AcademicYear] Error writing to localStorage:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AcademicYear', { context: 'write localStorage' });
    }
  }, []);

  // Check for global settings when user roles are available
  useEffect(() => {
    const checkGlobalSettings = async () => {
      if (!userRoles || userRoles.length === 0 || globalSettingsChecked) {
        return;
      }

      const isSchoolUser = userRoles.some(role => 
        ['school', 'school_admin', 'school_staff'].includes(role)
      );
      
      const isAdminUser = userRoles.some(role => 
        ['admin', 'super_admin', 'district', 'district_admin'].includes(role)
      );

      if (isSchoolUser) {
        try {
          const { data: globalYearData } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'global_school_dashboard_year')
            .maybeSingle();

          const { data: overrideData } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'allow_school_year_override')
            .maybeSingle();

          const { data: adjacentYearsData } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'allow_school_adjacent_years')
            .maybeSingle();

          const globalYear = typeof globalYearData?.value === 'string'
            ? globalYearData.value.replace(/^"|"$/g, '') // Remove quotes if present
            : globalYearData?.value;
          const allowOverride = overrideData?.value === true || overrideData?.value === 'true';
          const allowAdjacentYears = adjacentYearsData?.value === true || adjacentYearsData?.value === 'true';

          setGlobalSchoolYear(globalYear);
          setAllowSchoolOverride(allowOverride);
          setAllowSchoolAdjacentYears(allowAdjacentYears);
          setGlobalSettingsChecked(true);

          // Use global year as default for school users if:
          // 1. Global year is set AND
          // 2. Either override is not allowed OR user hasn't explicitly selected a year yet
          if (globalYear && (!allowOverride || !yearHasBeenSetRef.current)) {
            setCurrentYearState(globalYear);
            writeToLocalStorage(globalYear);
            yearHasBeenSetRef.current = true;
          }
        } catch (error) {
          console.error('[AcademicYear] Error checking global settings:', error);
          EnhancedErrorLogger.getInstance().logQuickError(error, 'AcademicYear', { context: 'check global settings' });
          setGlobalSettingsChecked(true);
        }
      } else if (isAdminUser) {
        try {
          const { data: globalYearData } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'global_admin_dashboard_year')
            .maybeSingle();

          const { data: overrideData } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'allow_admin_year_override')
            .maybeSingle();

          const { data: adminAdjacentYearsData } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'allow_admin_adjacent_years')
            .maybeSingle();

          const globalYear = typeof globalYearData?.value === 'string'
            ? globalYearData.value.replace(/^"|"$/g, '') // Remove quotes if present
            : globalYearData?.value;
          const allowOverride = overrideData?.value === true || overrideData?.value === 'true';
          const allowAdjacentYears = adminAdjacentYearsData?.value === true || adminAdjacentYearsData?.value === 'true';

          setGlobalAdminYear(globalYear);
          setAllowAdminOverride(allowOverride);
          setAllowAdminAdjacentYears(allowAdjacentYears);
          setGlobalSettingsChecked(true);

          // Use global year as default for admin users if:
          // 1. Global year is set AND
          // 2. Either override is not allowed OR user hasn't explicitly selected a year yet
          if (globalYear && (!allowOverride || !yearHasBeenSetRef.current)) {
            setCurrentYearState(globalYear);
            writeToLocalStorage(globalYear);
            yearHasBeenSetRef.current = true;
          }
        } catch (error) {
          console.error('[AcademicYear] Error checking global admin settings:', error);
          EnhancedErrorLogger.getInstance().logQuickError(error, 'AcademicYear', { context: 'check admin settings' });
          setGlobalSettingsChecked(true);
        }
      } else {
        setGlobalSettingsChecked(true);
      }
    };

    checkGlobalSettings();
  }, [userRoles, globalSettingsChecked, currentYear, writeToLocalStorage]);

  // Initialize from user preferences, then localStorage, then fallback
  useEffect(() => {
    if (!initialLoadComplete && !isLoadingPreferences) {
      const initializeYear = async () => {
        // Priority 1: User's database preference
        if (userSelectedYear) {
          setCurrentYearState(userSelectedYear);
          yearHasBeenSetRef.current = true;
        } else {
          // Priority 2: LocalStorage (for backwards compatibility)
          const storedYear = readFromLocalStorage();

          if (storedYear) {
            setCurrentYearState(storedYear);
            yearHasBeenSetRef.current = true;
            // Save to database for future persistence only if authenticated
            if (isAuthenticated) {
              updateSelectedYear(storedYear);
            }
          } else {
            // Priority 3: API current year or calculated fallback
            // NOTE: Don't set yearHasBeenSetRef here - this is just a temporary fallback
            // that can be overridden by global settings once they load
            const fallbackYear = apiCurrentYear || getCurrentApplicationYear();
            setCurrentYearState(fallbackYear);
            // yearHasBeenSetRef stays false so global settings can override
          }
        }
        setInitialLoadComplete(true);
      };

      initializeYear();
    }
  }, [initialLoadComplete, isLoadingPreferences, userSelectedYear, apiCurrentYear, readFromLocalStorage, updateSelectedYear, isAuthenticated]);

  // Update from API when data arrives
  // Only set API year if user hasn't explicitly selected a different year
  useEffect(() => {
    // Skip if user has already explicitly selected a year - respect their choice
    if (yearHasBeenSetRef.current) {
      return;
    }

    // If we already have a current year set (from localStorage/preferences), don't override
    if (currentYear) {
      return;
    }

    // Only set API year if we don't have a current year set
    if (!currentYear && apiCurrentYear) {
      setCurrentYearState(apiCurrentYear);
      writeToLocalStorage(apiCurrentYear);
      yearHasBeenSetRef.current = true;
    } else if (!isLoadingAPI && !apiError && availableYears.length > 0 && !currentYear) {
      // If API doesn't provide current year but has available years,
      // check if our current year is valid, otherwise use the first available
      const storedYear = readFromLocalStorage();
      if (storedYear && availableYears.includes(storedYear)) {
        setCurrentYearState(storedYear);
        yearHasBeenSetRef.current = true;
      } else {
        // Use the most recent year from available years
        const sortedYears = [...availableYears].sort((a, b) => b.localeCompare(a));
        const mostRecentYear = sortedYears[0];
        setCurrentYearState(mostRecentYear);
        writeToLocalStorage(mostRecentYear);
        yearHasBeenSetRef.current = true;
      }
    }
  }, [apiCurrentYear, availableYears, isLoadingAPI, apiError, currentYear, writeToLocalStorage, readFromLocalStorage]);

  // Sync localStorage to database once authenticated with proper safeguards
  useEffect(() => {
    // Prevent sync if:
    // 1. Already synced in this session
    // 2. Too many sync attempts (prevent infinite loop)
    // 3. No current year available
    // 4. User already has a preference
    if (
      isAuthenticated && 
      !userSelectedYear && 
      currentYear && 
      !hasSyncedToDatabase && 
      syncAttempts < 3
    ) {
      setSyncAttempts(prev => prev + 1);
      
      // Mark as synced immediately to prevent multiple calls
      setHasSyncedToDatabase(true);
      
      // Attempt to save to database with a small delay to prevent rapid-fire requests
      const timeoutId = setTimeout(() => {
        updateSelectedYear(currentYear);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, userSelectedYear, currentYear, hasSyncedToDatabase, syncAttempts, updateSelectedYear]);

  // Track previous auth state to detect actual logout (not initial load)
  const wasAuthenticatedRef = useRef<boolean | null>(null);

  // Reset sync flag when user logs out (not on initial load)
  useEffect(() => {
    // On first run, just record the state
    if (wasAuthenticatedRef.current === null) {
      wasAuthenticatedRef.current = isAuthenticated;
      return;
    }

    // Only reset when going from authenticated -> not authenticated (actual logout)
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      setHasSyncedToDatabase(false);
      setSyncAttempts(0);
      // Only reset the year-set ref on actual logout, not initial load
      // This preserves localStorage year during initial auth loading
      yearHasBeenSetRef.current = false;
    }

    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Refusing silently is what made the year picker look broken: the district
  // can pin school users (or admins) to a single year, and every one of those
  // gates simply returned. Nothing was logged, nothing was shown, and the
  // dropdown just did not move. The rule stays — it is deliberate — but it now
  // says so, the same way the availability check twenty lines below already
  // does.
  const refuseYearChange = useCallback((year: string, lockedTo: string | null, who: string) => {
    console.warn(
      `[AcademicYear] ${who} are locked to ${lockedTo ?? 'a fixed year'}; refused ${year}`,
    );
    toast.error(
      lockedTo
        ? `Your district has set the school year to ${lockedTo}. Contact NWSISD if you need a different year.`
        : 'Your district has locked the school year. Contact NWSISD if you need a different year.',
    );
  }, []);

  // Update year function that syncs with localStorage and database
  const setCurrentYear = useCallback((year: string) => {
    // Check if user is in a school role and if override is allowed
    const isSchoolUser = userRoles?.some(role => 
      ['school', 'school_admin', 'school_staff'].includes(role)
    );
    
    const isAdminUser = userRoles?.some(role => 
      ['admin', 'super_admin', 'district', 'district_admin'].includes(role)
    );

    if (isSchoolUser && globalSchoolYear && !allowSchoolOverride) {
      // The pinned year is always permitted: it is the district's own choice.
      // Deriving the allowed list from the active-year query alone refused it
      // whenever the pinned year was missing from that list — and since the
      // refusal now speaks, it told the user to pick the very year they had
      // just picked. Everything else comes from the adjacency rule.
      const allowedYears = [
        globalSchoolYear,
        ...filterYearsForUser(
          allAvailableYears,
          globalSchoolYear,
          allowSchoolOverride,
          allowSchoolAdjacentYears
        ),
      ];
      if (!allowedYears.includes(year)) {
        refuseYearChange(year, globalSchoolYear, 'School users');
        return;
      }
    }
    
    if (isAdminUser && globalAdminYear && !allowAdminOverride) {
      // Same shape as the school gate above: the pinned year is always allowed.
      const allowedYears = [
        globalAdminYear,
        ...filterYearsForUser(
          allAvailableYears,
          globalAdminYear,
          allowAdminOverride,
          allowAdminAdjacentYears
        ),
      ];
      if (!allowedYears.includes(year)) {
        refuseYearChange(year, globalAdminYear, 'Admins');
        return;
      }
    }

    // Validate that the year is available if we have API data
    if (availableYears.length > 0 && !availableYears.includes(year)) {
      console.warn(`[AcademicYear] Year ${year} is not in available years:`, availableYears);
      // Still allow setting for offline/fallback scenarios
    }

    const yearChanged = year !== currentYear;

    setCurrentYearState(year);
    writeToLocalStorage(year);
    // Mark that user has explicitly set a year - prevents reset during operations
    yearHasBeenSetRef.current = true;
    // Save to database for persistence across sessions/devices
    updateSelectedYear(year);

    // The active academic year is a cross-cutting dimension: nearly every data view
    // (applications, capacity, analytics, students, transportation) is scoped to it, yet
    // many query keys do NOT encode the year. Without invalidation, React Query keeps
    // serving the previous year's cached data after a switch — showing one year's records
    // under another year's heading. Invalidate all queries so every active view refetches
    // for the newly selected year. Guarded on an actual change to avoid needless refetches.
    if (yearChanged) {
      queryClient.invalidateQueries();
    }
  }, [currentYear, queryClient, availableYears, writeToLocalStorage, updateSelectedYear, refuseYearChange, userRoles, globalSchoolYear, allowSchoolOverride, allowSchoolAdjacentYears, globalAdminYear, allowAdminOverride, allowAdminAdjacentYears, allAvailableYears]);

  // Determine loading state
  // We're loading during initial setup, API calls, or preferences loading
  const isLoading = !initialLoadComplete || isLoadingPreferences || (isLoadingAPI && !currentYear);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo<AcademicYearContextType>(() => ({
    currentYear,
    setCurrentYear,
    isLoading,
    error: apiError,
    refetch,
    availableYears,
    globalSchoolYear,
    allowSchoolOverride,
    allowSchoolAdjacentYears,
    globalAdminYear,
    allowAdminOverride,
    allowAdminAdjacentYears
  }), [
    currentYear,
    setCurrentYear,
    isLoading,
    apiError,
    refetch,
    availableYears,
    globalSchoolYear,
    allowSchoolOverride,
    allowSchoolAdjacentYears,
    globalAdminYear,
    allowAdminOverride,
    allowAdminAdjacentYears
  ]);

  return (
    <AcademicYearContext.Provider value={contextValue}>
      {children}
    </AcademicYearContext.Provider>
  );
};

export const useAcademicYear = () => {
  const context = useContext(AcademicYearContext);
  if (context === undefined) {
    throw new Error('useAcademicYear must be used within an AcademicYearProvider');
  }
  return context;
};